import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { caseStudies, profile } from "../data/content";

const SectionLabel = ({ children }) => (
  <h2 className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.28em] text-faint">
    <span className="h-px w-8 bg-teal/60" aria-hidden="true" />
    {children}
  </h2>
);

export default function CaseStudy() {
  const { slug } = useParams();
  const study = caseStudies.find((s) => s.slug === slug);

  if (!study) {
    return (
      <div className="bg-canvas text-ink font-sans min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="font-display text-2xl">Case study not found.</p>
          <Link to="/" className="mt-4 inline-block text-sm text-teal link-underline" data-testid="not-found-back-link">
            Back to portfolio
          </Link>
        </div>
      </div>
    );
  }

  const next = caseStudies.find((s) => s.slug !== slug);

  return (
    <div className="bg-canvas text-ink font-sans antialiased min-h-screen">
      <header className="sticky top-0 z-50 bg-canvas/95 border-b border-hairline">
        <div className="container-x h-16 flex items-center justify-between">
          <Link
            to="/"
            data-testid="case-study-logo"
            className="w-9 h-9 border border-cardline bg-panel flex items-center justify-center font-display text-sm font-semibold text-ink hover:border-teal hover:text-teal transition-colors duration-300"
            aria-label="Back to portfolio"
          >
            {profile.initials}
          </Link>
          <Link
            to="/"
            data-testid="back-to-portfolio-link"
            className="inline-flex items-center gap-2 text-sm text-faint hover:text-teal transition-colors duration-300"
          >
            <ArrowLeft size={15} /> Back to portfolio
          </Link>
        </div>
      </header>

      <main className="container-x py-16 sm:py-24" data-testid="case-study-page">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-teal">Case Study {study.index}</p>
          <h1
            className="mt-6 font-display text-4xl sm:text-5xl font-semibold tracking-tight text-ink max-w-3xl leading-[1.08]"
            data-testid="case-study-title"
          >
            {study.title}
          </h1>
          <p className="mt-5 text-sm text-faint font-mono tracking-wide">{study.context}</p>
        </Reveal>

        <Reveal delay={0.08}>
          <div className="mt-8 flex flex-wrap gap-2" data-testid="case-study-stack">
            {study.stack.map((s) => (
              <span key={s} className="font-mono text-[11px] tracking-wide text-teal bg-teal-light px-3 py-1.5">
                {s}
              </span>
            ))}
          </div>
        </Reveal>

        <div className="mt-16 sm:mt-20 space-y-16 max-w-3xl">
          <Reveal>
            <section data-testid="case-study-problem">
              <SectionLabel>The problem</SectionLabel>
              <p className="mt-6 font-display text-xl sm:text-2xl text-ink leading-snug">{study.problem}</p>
            </section>
          </Reveal>

          <Reveal>
            <section data-testid="case-study-process">
              <SectionLabel>The process</SectionLabel>
              <ol className="mt-6 space-y-5">
                {study.process.map((step, i) => (
                  <li key={i} className="flex gap-5">
                    <span className="font-mono text-xs text-teal pt-1.5 shrink-0">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <p className="text-base sm:text-lg text-body leading-relaxed">{step}</p>
                  </li>
                ))}
              </ol>
            </section>
          </Reveal>

          <Reveal>
            <section data-testid="case-study-results">
              <SectionLabel>The results</SectionLabel>
              <ul className="mt-6 space-y-4">
                {study.results.map((r, i) => (
                  <li key={i} className="pl-4 border-l-2 border-teal text-base sm:text-lg text-ink leading-relaxed">
                    {r}
                  </li>
                ))}
              </ul>
            </section>
          </Reveal>
        </div>

        {next && (
          <Reveal>
            <Link
              to={`/work/${next.slug}`}
              data-testid="next-case-study-link"
              className="group mt-24 block bg-panelF border border-cardline p-8 sm:p-10 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_12px_32px_-16px_rgba(28,28,28,0.18)]"
            >
              <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-faint">Next case study</span>
              <span className="mt-4 flex items-center justify-between gap-4">
                <span className="font-display text-xl sm:text-2xl font-semibold tracking-tight text-ink">
                  {next.title}
                </span>
                <ArrowRight
                  size={20}
                  className="text-faint group-hover:text-teal group-hover:translate-x-1 transition-all duration-300 shrink-0"
                />
              </span>
            </Link>
          </Reveal>
        )}
      </main>

      <footer className="border-t border-hairline">
        <div className="container-x py-8">
          <p className="text-sm text-faint">© 2026 Ritu Mali · San Diego, CA</p>
        </div>
      </footer>
    </div>
  );
}
